/**
 * Gift-amount options for the impact filter and the madlib sentence.
 *
 * Figma references:
 *   - Madlib chip shows "$5,000,000" as the default (node 59:24173).
 *   - The lead-gen "grey band" cites $1,000,000 (node 111:32298).
 *
 * Values below are the only amounts explicitly present in the design.
 * TODO(figma): confirm the full set of allowed amounts with product.
 */

export interface GiftAmount {
  /** Stable identifier — used as the underlying select value. */
  id: string;
  /** Raw amount in USD, useful for downstream calculations. */
  value: number;
  /** Human-readable label — matches the Figma copy exactly. */
  label: string;
}

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/**
 * Build a `GiftAmount` from a raw USD number so the label formatting stays
 * consistent regardless of where new tiers are added.
 */
function amount(id: string, value: number): GiftAmount {
  return { id, value, label: usdFormatter.format(value) };
}

export const GIFT_AMOUNTS: readonly GiftAmount[] = [
  amount('100k', 100_000),
  amount('500k', 500_000),
  amount('1m', 1_000_000),
  amount('5m', 5_000_000),
  amount('7.5m', 7_500_000),
  amount('10m', 10_000_000),
] as const;

/**
 * Default amount shown in the madlib sentence in Figma ("$5,000,000").
 */
export const DEFAULT_GIFT_AMOUNT_ID: GiftAmount['id'] = '5m';
